import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronRight, Search } from 'lucide-react';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { Button } from '@shared/ui/primitives/Button';
import { initialsOf } from '@shared/lib/format';
import { cn } from '@shared/lib/cn';
import { haptics } from '@shared/lib/haptics';
import { VISITOR_GROUPS_QUERY_KEY, fetchVisitorGroups, type VisitorGroup } from './memberAppApi';
import { MEMBER_CONTACTS_QUERY_KEY, fetchMemberContacts, type MemberContact, type Person } from './memberContactsApi';
import { firstName } from './NewPinSheet';
import { ListSheet } from './visitor-access/ListSheet';
import { fetchAccessEntryTypesForInvite } from './api';
import { contactLine } from './visitorAccessFormat';
import { HELP, LABEL } from './visitorAccessStyles';

/** How many people one go may be for, from the entry type's saved groups setting. */
export interface GroupSize {
  min: number;
  max: number;
}

/** A group, a person, or Add a person: one of the picker's bordered rows. */
export const PICKER_ROW = 'flex min-h-16 w-full items-center gap-3 rounded-[14px] border border-hairline bg-card px-3.5 text-left text-ink';

/** How many groups, and contacts, show before View all. */
const FEW_GROUPS = 3;
const FEW_CONTACTS = 5;

interface PeoplePickerProps {
  /** Who can get this PIN: someone whose own type uses another gate system is greyed out. */
  allowed: (person: Person) => boolean;
  /** People to list first, such as those just added. */
  first?: string[];
  /** What a person lacks for this type, which keeps them from being ticked. */
  blockedReason: (person: Person) => string | undefined;
  /** Null when the type's saved groups are off: no groups, and one person at a time. */
  groupSize: GroupSize | null;
  /** The type, named in the size limits ("Visitor allows up to 5 people"). */
  typeName: string;
  /** Who is ticked, and the group they were ticked from, kept by the steps so going back finds them again. */
  ticked: string[];
  onTick: (ids: string[]) => void;
  /** The group open in its own sheet, kept by the steps so going back opens it again; undefined once closed. */
  group?: VisitorGroup;
  onGroup: (group?: VisitorGroup) => void;
  /** A person who lacks something was tapped: the steps say what to fix. */
  onBlocked: (person: Person) => void;
  onContinue: (people: Person[]) => void;
  /** Shown when the member has nobody saved. */
  emptyText: string;
  /** Rows after the contacts, such as Someone new. */
  children?: ReactNode;
}

/**
 * Who the PINs are for, in two parts: Groups (the first few, View all for the rest) and Contacts (a
 * search and the most recent few, View all for everyone). Anyone whose type uses another gate system
 * is greyed out, and a group says how many of its people can come. A group opens in its own sheet on
 * everyone in it who can get a PIN, to untick anyone not coming; swiping it away goes back to the list.
 * One person is a normal PIN; two or more must fit the type's group size.
 */
export function PeoplePicker({
  allowed,
  first = [],
  blockedReason,
  groupSize,
  typeName,
  ticked,
  onTick,
  group,
  onGroup,
  onBlocked,
  onContinue,
  emptyText,
  children,
}: PeoplePickerProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [allOpen, setAllOpen] = useState<'groups' | 'contacts'>();
  const [allSearch, setAllSearch] = useState('');
  // Who was ticked before a group opened, ticked again if it is closed without going on.
  const [beforeGroup, setBeforeGroup] = useState<string[]>();
  const peopleQuery = useQuery({ queryKey: MEMBER_CONTACTS_QUERY_KEY, queryFn: fetchMemberContacts });
  const typesQuery = useQuery({ queryKey: ['access-entry-types'], queryFn: fetchAccessEntryTypesForInvite });
  const groupsQuery = useQuery({ queryKey: VISITOR_GROUPS_QUERY_KEY, queryFn: fetchVisitorGroups, enabled: Boolean(groupSize) });

  if (peopleQuery.isLoading || groupsQuery.isLoading) return <Skeleton className="h-40 rounded-lg" />;
  if (peopleQuery.isError || groupsQuery.isError)
    return <ErrorState onRetry={() => void Promise.all([peopleQuery.refetch(), groupsQuery.refetch()])} />;

  // 1. Everyone not archived: those just added, then whoever last had a PIN, then by name.
  const rank = (person: MemberContact) => {
    const index = first.indexOf(person.member_contact_id);
    return index < 0 ? first.length : index;
  };
  const everyone = (peopleQuery.data ?? [])
    .filter((person) => person.is_active)
    .sort((a, b) => rank(a) - rank(b) || (b.last_pin_at ?? '').localeCompare(a.last_pin_at ?? ''));
  const byId = (id: string) => everyone.find((person) => person.member_contact_id === id);
  const groups = groupSize ? (groupsQuery.data ?? []) : [];
  const matches = (name: string, term: string) => name.toLowerCase().includes(term.trim().toLowerCase());

  // 2. Contacts: a search finds anyone; otherwise the most recent few, plus anyone ticked from View all.
  const contacts = search.trim()
    ? everyone.filter((person) => matches(person.display_name, search))
    : everyone.filter((person, index) => index < FEW_CONTACTS || ticked.includes(person.member_contact_id));
  const members = group ? group.members.flatMap(({ member_contact_id }) => byId(member_contact_id) ?? []) : [];
  const chosen = (group ? members : everyone).filter((person) => ticked.includes(person.member_contact_id));

  // 3. One person is always fine; two or more must fit the type's group size. Groups can be off with
  //    several still ticked, when a guest stay moves to a property without them.
  const count = chosen.length;
  const fits = count === 1 || (groupSize ? count >= groupSize.min && count <= groupSize.max : false);
  const help =
    !count || fits
      ? ''
      : !groupSize
        ? `${typeName} takes one person at a time here. Untick ${count - 1} more.`
        : count > groupSize.max
          ? `${typeName} allows up to ${groupSize.max} people. Untick ${count - groupSize.max} more.`
          : `${typeName} needs at least ${groupSize.min} people. Tick ${groupSize.min - count} more, or just one.`;

  // 4. Ticking adds or removes; with groups off a tick replaces the last.
  const toggle = (person: Person) => {
    const id = person.member_contact_id;
    haptics.tap();
    if (ticked.includes(id)) onTick(ticked.filter((value) => value !== id));
    else onTick(groupSize ? ticked.concat(id) : [id]);
  };

  // 5. A group opens with everyone in it who can get a PIN already ticked.
  const canCome = (picked: VisitorGroup) =>
    picked.members.flatMap(({ member_contact_id }) => {
      const person = byId(member_contact_id);
      return person && allowed(person) ? [person] : [];
    });
  const openGroup = (picked: VisitorGroup) => {
    setAllOpen(undefined);
    setBeforeGroup(ticked);
    onGroup(picked);
    onTick(canCome(picked).flatMap((person) => (blockedReason(person) ? [] : [person.member_contact_id])));
  };
  const closeGroup = () => {
    onGroup(undefined);
    if (beforeGroup) onTick(beforeGroup);
    setBeforeGroup(undefined);
  };

  const groupRow = (row: VisitorGroup) => {
    const total = row.members.length;
    const able = canCome(row).length;
    return (
      <button
        key={row.access_visitor_group_id}
        type="button"
        disabled={!able}
        onClick={() => openGroup(row)}
        className={cn(PICKER_ROW, !able && 'opacity-50')}
      >
        <span className="flex w-[66px] shrink-0">
          {row.members.slice(0, 3).map((member) => (
            <span
              key={member.member_contact_id}
              className="-ml-2.5 flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-card bg-primary-50 text-[11px] font-bold text-primary-700 first:ml-0"
            >
              {initialsOf(member.display_name)}
            </span>
          ))}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 py-2.5">
          <span className="truncate text-[15px] font-semibold">{row.name}</span>
          <span className="text-[13px] text-ink-secondary">
            {!able ? 'None can come on this PIN' : able < total ? `${able} of ${total} can come` : peopleCount(total)}
            {groupSize && able > groupSize.max ? ` · ${typeName} allows up to ${groupSize.max}` : ''}
          </span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} aria-hidden />
      </button>
    );
  };

  // 6. A person: tapping ticks them. Another gate system greys them out; in a group, so does what they lack.
  const personRow = (person: Person, onLacking: (person: Person) => void, inGroup = false) => {
    const on = ticked.includes(person.member_contact_id);
    const line = contactLine(person, typesQuery.data);
    const reason = allowed(person) ? blockedReason(person) : `${line} · other gate system`;
    const locked = !allowed(person) || Boolean(inGroup && reason);
    return (
      <button
        key={person.member_contact_id}
        type="button"
        role="checkbox"
        aria-checked={on}
        disabled={locked}
        onClick={() => (reason ? onLacking(person) : toggle(person))}
        className={cn(PICKER_ROW, on && 'border-primary-500 bg-primary-50', locked && 'opacity-50')}
      >
        <span
          className={cn(
            'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[6px] border-2',
            on ? 'border-primary-500 bg-primary-500 text-primary-ink' : 'border-hairline bg-card',
          )}
        >
          {on ? <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden /> : null}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 py-2.5">
          <span className="truncate text-[15px] font-semibold">{person.display_name}</span>
          <span className={cn('text-[13px]', inGroup && reason && allowed(person) ? 'text-danger' : 'text-ink-secondary')}>
            {inGroup && reason && allowed(person) ? `${reason}. Add it under Contacts to include them.` : (reason ?? line)}
          </span>
        </span>
      </button>
    );
  };

  const openAll = (which: 'groups' | 'contacts') => {
    setAllSearch('');
    setAllOpen(which);
  };

  return (
    <div className="flex flex-1 flex-col gap-2.5">
      {/* Groups: the first few, and View all for the rest. */}
      {groups.length ? (
        <>
          <p className={cn(LABEL, 'mt-1.5')}>Groups</p>
          {groups.slice(0, FEW_GROUPS).map(groupRow)}
          {groups.length > FEW_GROUPS ? (
            <ViewAll onClick={() => openAll('groups')}>View all {groups.length} groups</ViewAll>
          ) : null}
        </>
      ) : null}

      {/* Contacts: a search, the most recent few, and View all for everyone. */}
      <p className={cn(LABEL, 'mt-1.5')}>Contacts</p>
      {everyone.length ? <SearchBox label="Search contacts" value={search} onChange={setSearch} /> : null}
      {contacts.map((person) => personRow(person, onBlocked))}
      {!everyone.length ? <p className="text-[0.8125rem] text-ink-secondary">{emptyText}</p> : null}
      {everyone.length && !contacts.length ? <p className={HELP}>Nobody by that name.</p> : null}
      {!search.trim() && everyone.length > FEW_CONTACTS ? (
        <ViewAll onClick={() => openAll('contacts')}>View all {everyone.length} contacts</ViewAll>
      ) : null}
      {children}

      {/* Continue, once anyone is ticked, with why it can't go on above it. */}
      {count > 0 && !group ? (
        <div className="sticky bottom-0 -mx-6 mt-auto flex flex-col gap-2 bg-elevated px-6 pb-4 pt-3 hairline-t">
          {help ? <p className={cn(HELP, 'text-center text-danger')}>{help}</p> : null}
          <ContinueButton count={count} fits={fits} onClick={() => onContinue(chosen)} />
        </div>
      ) : null}

      {/* A group in its own sheet: its people, those who can't come greyed out. Swiping it away goes back. */}
      <ListSheet
        open={Boolean(group)}
        onOpenChange={(open) => !open && closeGroup()}
        title={group ? `Who from ${group.name} is coming?` : ''}
        footer={
          <>
            {help ? <p className={cn(HELP, 'text-center text-danger')}>{help}</p> : null}
            <ContinueButton count={count} fits={count > 0 && fits} onClick={() => onContinue(chosen)} />
          </>
        }
      >
        <div className="flex flex-col gap-2.5">
          <p className="m-0 text-[0.8125rem] leading-snug text-ink-secondary">
            Everyone ticked gets their own PIN. Untick anyone not coming this time.
          </p>
          {members.map((person) => personRow(person, onBlocked, true))}
        </div>
      </ListSheet>

      {/* View all: every group to open one, or every contact to tick, each with a search. */}
      <ListSheet
        open={Boolean(allOpen)}
        onOpenChange={(open) => !open && setAllOpen(undefined)}
        title={allOpen === 'groups' ? 'Your groups' : 'Your contacts'}
        footer={
          allOpen === 'contacts' ? (
            <Button width="full" onClick={() => setAllOpen(undefined)}>
              {count ? `Done · ${peopleCount(count)} ticked` : 'Done'}
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-2.5">
          <SearchBox label={allOpen === 'groups' ? 'Search groups' : 'Search contacts'} value={allSearch} onChange={setAllSearch} />
          {allOpen === 'groups' ? (
            <>
              {groups.filter((row) => matches(row.name, allSearch)).map(groupRow)}
              {!groups.some((row) => matches(row.name, allSearch)) ? <p className={HELP}>No group by that name.</p> : null}
            </>
          ) : (
            <>
              {everyone
                .filter((person) => matches(person.display_name, allSearch))
                .map((person) =>
                  personRow(person, (lacking) => {
                    setAllOpen(undefined);
                    onBlocked(lacking);
                  }),
                )}
              {!everyone.some((person) => matches(person.display_name, allSearch)) ? (
                <p className={HELP}>Nobody by that name.</p>
              ) : null}
            </>
          )}
        </div>
      </ListSheet>
    </div>
  );
}

function SearchBox({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }): JSX.Element {
  return (
    <label className="flex min-h-[46px] items-center gap-2 rounded-[12px] border border-hairline bg-card px-3.5">
      <Search className="h-[18px] w-[18px] shrink-0 text-ink-muted" strokeWidth={2} aria-hidden />
      <input
        type="search"
        aria-label={label}
        placeholder={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-muted"
      />
    </label>
  );
}

function ViewAll({ onClick, children }: { onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button type="button" onClick={onClick} className="min-h-11 self-start px-1 text-sm font-semibold text-primary-500">
      {children}
    </button>
  );
}

function ContinueButton({ count, fits, onClick }: { count: number; fits: boolean; onClick: () => void }): JSX.Element {
  return (
    <Button width="full" disabled={!fits} onClick={onClick}>
      {count > 1 ? `Continue with ${count} people` : 'Continue'}
    </Button>
  );
}

/** "Ian, Joshua and Matthew". */
export function joinFirstNames(people: Person[]): string {
  const names = people.map((person) => firstName(person.display_name));
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function peopleCount(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`;
}
